"use client";

import { useEffect, useState } from "react";
import type { NavigationCartItem } from "@/scott/ui/layout/main-navigation/navigation-cart/navigation-cart";
import { subscribeToCartUpdates } from "@/utils/accelerator-cart.utils";
import { fetchAcceleratorCartItems } from "@/utils/accelerator-cart-items.utils";

/**
 * Reads the entries of the header cart drawer. They are loaded when the drawer
 * opens and, while it stays open, refreshed on the same cart updates as the
 * header count (see useAcceleratorCartCount), so both show the same cart.
 */
export function useNavigationCartItems(
  country: string,
  language: string,
  isOpen: boolean
): NavigationCartItem[] {
  const [items, setItems] = useState<NavigationCartItem[]>([]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isActive = true;
    let latestRefreshId = 0;

    async function refresh() {
      const refreshId = ++latestRefreshId;

      try {
        const nextItems = await fetchAcceleratorCartItems(country, language);

        if (isActive && refreshId === latestRefreshId) {
          setItems(nextItems);
        }
      } catch {
        // Keeps the last known entries; the next cart update retries.
      }
    }

    refresh();
    const unsubscribe = subscribeToCartUpdates(refresh);

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [country, language, isOpen]);

  return items;
}
